// useBarraGrudada.js — a regra das barras que acompanham a rolagem.
//
// ================= O PROBLEMA QUE ELA RESOLVE =================
//
// O cabeçalho do site já vai e volta sozinho: sai ao rolar para baixo, volta ao
// rolar para cima (useNavbarOculta). Quando uma segunda barra gruda no topo — a
// do nome no perfil, a da busca na estante —, as duas se empilham justamente no
// gesto de subir, e sobra meia tela de conteúdo.
//
// A regra, pedida pelo dono em 11/09/2026: enquanto o cabeçalho do site estiver
// à vista, a barra de dentro SOME. Ela só assume o topo quando o cabeçalho sai.
// No alto da página nada disso vale — ali a barra está na posição natural dela,
// não grudada, e as duas convivem.
//
// ================= POR QUE UMA SENTINELA =================
//
// "Está grudada?" não pode ser medido na própria barra: ela é deslocada por
// `transform` quando se esconde, e `getBoundingClientRect` devolveria a posição
// do disfarce — a barra ficaria presa escondida ao voltar para o topo. A
// sentinela é um elemento de altura zero que marca onde a barra NASCE e nunca
// se move.
//
// Devolve os dois `ref` (um para a barra, um para a sentinela), a ALTURA medida
// da barra (quem gruda embaixo dela precisa somá-la) e `esconder`.
import { useEffect, useMemo, useRef, useState } from "react";

export default function useBarraGrudada({ margemNegativa = 0, quando = true } = {}) {
  const barraRef = useRef(null);
  const sentinelaRef = useRef(null);
  const [altura, setAltura] = useState(0);
  const [presa, setPresa] = useState(false);
  const [navOculto, setNavOculto] = useState(false);

  // A altura é MEDIDA porque muda com o conteúdo (selos do perfil, chips de
  // filtro que quebram em duas linhas) e com a largura da tela.
  useEffect(() => {
    const el = barraRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setAltura(el.offsetHeight));
    ro.observe(el);
    setAltura(el.offsetHeight);
    return () => ro.disconnect();
  }, [quando]);

  useEffect(() => {
    if (!quando) return;

    const raiz = document.documentElement;
    const lerNav = () => setNavOculto(raiz.classList.contains("nav-oculto"));
    lerNav();
    // Observar a classe do cabeçalho é mais fiel do que repetir aqui a regra de
    // "para que lado a pessoa está rolando" — e continua certo se aquela regra
    // mudar um dia.
    const observador = new MutationObserver(lerNav);
    observador.observe(raiz, { attributes: true, attributeFilter: ["class"] });

    let agendado = false;
    const avaliar = () => {
      agendado = false;
      const sentinela = sentinelaRef.current;
      const el = barraRef.current;
      if (!sentinela || !el) return;
      const topo = parseFloat(window.getComputedStyle(el).top) || 0;
      const nascePor = sentinela.getBoundingClientRect().top + window.scrollY - margemNegativa;
      setPresa(window.scrollY + topo >= nascePor);
    };
    const aoRolar = () => {
      if (agendado) return;
      agendado = true;
      window.requestAnimationFrame(avaliar);
    };

    window.addEventListener("scroll", aoRolar, { passive: true });
    window.addEventListener("resize", aoRolar);
    avaliar();
    return () => {
      observador.disconnect();
      window.removeEventListener("scroll", aoRolar);
      window.removeEventListener("resize", aoRolar);
    };
  }, [quando, margemNegativa]);

  return {
    barraRef,
    sentinelaRef,
    altura,
    presa,
    // Só some quando ESTÁ grudada.
    esconder: useMemo(() => presa && !navOculto, [presa, navOculto]),
  };
}

/** As classes do sumiço, iguais nas duas barras (e só do tablet para cima). */
export const CLASSES_SUMICO =
  "md:transition-[top,transform,opacity] md:duration-[380ms] md:ease-in-out md:motion-reduce:transition-none";
export const CLASSES_ESCONDIDA = "md:-translate-y-[150%] md:opacity-0 md:pointer-events-none";
